import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';

import ModalWrapper from 'components/Modals/ModalWrapper';
import { RenderDatePicker } from 'components/Common/Inputs';
import DateTimes from 'components/Common/DateTimes';

const PublishedVersionsUpdateModal = ({
  title,
  isShown,
  onConfirm,
  onCancel,
  error,
  versionWindows,
  publishedVersionIndex,
  versionNumber
}) => {
  const defaultEffectiveFrom = publishedVersionIndex >= 0 && versionWindows && versionWindows.length > 0 && versionWindows[publishedVersionIndex].effectiveFrom;
  const defaultEffectiveTo = publishedVersionIndex >= 0 && versionWindows && versionWindows.length > 0 && versionWindows[publishedVersionIndex].effectiveTo;

  const [newEffectiveFromDate, setNewEffectiveFromDate] = useState(moment(defaultEffectiveFrom, 'YYYY-MM-DD').isValid() ? moment(defaultEffectiveFrom).format('MM-DD-YYYY') : '');
  const [newEffectiveToDate, setNewEffectiveToDate] = useState(moment(defaultEffectiveTo, 'YYYY-MM-DD').isValid() ? moment(defaultEffectiveTo).format('MM-DD-YYYY') : '');
  const [showInfoMessage, setShowInfoMessage] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const overlappingDateCheck = (newEffectiveFromDateMoment, newEffectiveToDateMoment, currentWindow) => (newEffectiveFromDateMoment <= moment(currentWindow.effectiveTo, 'YYYY-MM-DD') || currentWindow.effectiveTo === null)
    && (newEffectiveToDateMoment >= moment(currentWindow.effectiveFrom, 'YYYY-MM-DD') || newEffectiveToDateMoment === null);

  const fullOverlappingDateCheck = (newEffectiveFromDateMoment, newEffectiveToDateMoment, currentWindow) => (newEffectiveFromDateMoment <= moment(currentWindow.effectiveFrom, 'YYYY-MM-DD') || currentWindow.effectiveTo === null)
    && (newEffectiveToDateMoment >= moment(currentWindow.effectiveTo, 'YYYY-MM-DD') || newEffectiveToDateMoment === null);

  const disableConfirm = () => isDisabled;

  useEffect(() => {
    if (newEffectiveFromDate !== '') {
      const newEffectiveFromDateMoment = moment(newEffectiveFromDate, 'MM-DD-YYYY');
      const newEffectiveToDateMoment = moment(newEffectiveToDate, 'MM-DD-YYYY');
      if (versionWindows != null) {
        const filteredVersions = versionWindows.filter(version => overlappingDateCheck(newEffectiveFromDateMoment, newEffectiveToDateMoment, version)).filter(version => version.version !== versionNumber);
        const overlappingVersions = versionWindows.filter(version => fullOverlappingDateCheck(newEffectiveFromDateMoment, newEffectiveToDateMoment, version))
          .filter(version => version.version !== versionNumber);

        if (!newEffectiveFromDateMoment.isValid()) {
          setIsDisabled(true);
          setShowInfoMessage(false);
          return;
        }

        if (overlappingVersions.length > 0) {
          setShowInfoMessage(true);
          setIsDisabled(true);
          return;
        }
        setIsDisabled(false);
        if (filteredVersions.length === 1) {
          setShowInfoMessage(true);
        }
        if (filteredVersions.length === 0) {
          setShowInfoMessage(false);
        }
      }
    }
  }, [newEffectiveFromDate, newEffectiveToDate]);

  const onConfirmEffectiveDate = () => {
    onConfirm({ effectiveFromDate: newEffectiveFromDate, effectiveToDate: newEffectiveToDate, index: publishedVersionIndex, version: versionNumber });
  };

  return (
    <ModalWrapper
      modalSize="confirm-effective-date-modal"
      headerClose={ false }
      isOpen={ isShown }
      title={ title }
      btnConfirmTitle="Update"
      btnConfirmClass="btn-danger"
      btnCancelTitle="Cancel"
      onConfirm={ onConfirmEffectiveDate }
      onCancel={ onCancel }
      disableConfirm={ disableConfirm }
    >
      {showInfoMessage && !isDisabled && <div className="col-12">
        <h2 className="toast toast-blue text-center bold">Previous Statements are effective on these dates. Publishing this Statement will overwrite pre-existing information on the specified days
        </h2>
        </div>}
      {showInfoMessage && isDisabled && <h2 className="toast toast-red text-center bold">Effective dates would replace an existing published statement. Edit statement dates before updating
        </h2>}
      <div className="columns">
        <div className="col-6 effective-date-datepicker">
          <p><b>Effective From:</b> (required)</p>
          <RenderDatePicker
            input={ { onChange: e => setNewEffectiveFromDate(e), value: newEffectiveFromDate } }
            placeholder="mm-dd-yyyy"
            dateFormat="MM-dd-yyyy"
            maxDate={ newEffectiveToDate && new Date(Date.parse(newEffectiveToDate)) < DateTimes.today ? new Date(Date.parse(newEffectiveToDate)) : DateTimes.today }
          />
        </div>
        <div className="col-6 effective-date-datepicker pl-2">
          <p><b>Effective To:</b> (optional)</p>
          <RenderDatePicker
            input={ { onChange: e => setNewEffectiveToDate(e), value: newEffectiveToDate && newEffectiveToDate.length > 0 ? newEffectiveToDate : '' } }
            placeholder="mm-dd-yyyy"
            dateFormat="MM-dd-yyyy"
            isClearable
            minDate={ newEffectiveFromDate ? new Date(Date.parse(newEffectiveFromDate)) : new Date() }
          />
          <p>Unless a date is set the statement will be published until a new statement provided</p>
        </div>
      </div>
      {
                error
                && <div className="toast toast-error">
                    {error}
                </div>
            }
    </ModalWrapper>
  );
};

PublishedVersionsUpdateModal.propTypes = {
  title: PropTypes.string,
  isShown: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  error: PropTypes.string,
  versionWindows: PropTypes.arrayOf(PropTypes.shape()),
  publishedVersionIndex: PropTypes.number,
  versionNumber: PropTypes.number,
};

PublishedVersionsUpdateModal.defaultProps = {
  title: null,
  isShown: false,
  error: null,
  onConfirm: null,
  onCancel: null,
  versionWindows: null,
  publishedVersionIndex: -1,
  versionNumber: -1
};

function mapStateToProps(state) {
  const {
    forms: {
      publishedVersionIndex,
      version
    }
  } = state;
  return {
    publishedVersionIndex,
    versionNumber: version
  };
}
export default connect(
  mapStateToProps,
  null
)(PublishedVersionsUpdateModal);
